import { theme, spacing, typography, isMobile } from '../styles/theme';
import SimpleHeader from '../components/SimpleHeader';

export default function FAQ() {
  const faqs = [
    {
      question: "What is Judgy?",
      answer: (
        <>
          Judgy is an AI-powered Magic: The Gathering rules assistant that helps you understand card interactions, resolve rules questions, and learn the game's mechanics. It's trained on the Comprehensive Rules and thousands of real ruling examples. Lean more on our <a href="/about" style={{ color: theme.brand.primary, textDecoration: 'none', fontWeight: typography.fontWeight.medium }}>About page</a>.
        </>
      )
    },
    {
      question: "Is Judgy free to use?",
      answer: (
        <>
          Yes! Judgy offers a generous free tier with 100,000 tokens per month (approximately 50 questions depending on complexity). This resets on the 1st of each month. Premium membership ($5/month) increases your limit to 1,000,000 tokens and adds advanced features like conversation history and faster responses, though some premium features <strong>are still in development</strong>.
        </>
      )
    },
    {
      question: "What are token limits and how do they work?",
      answer: (
        <>
          Judgy measures usage in tokens (roughly 4 characters = 1 token). Free users get 100,000 tokens per month, while Premium users get 1,000,000 tokens. Complex questions with multiple cards use more tokens than simple ones. You'll see a warning when you've used 80% of your monthly limit, and your usage resets automatically on the 1st of each month.
        </>
      )
    },
    {
      question: "How accurate is Judgy?",
      answer: (
        <>
          Judgy is trained on official rules and real examples, but it's an AI tool not a certified judge. While it's highly accurate for most questions, complex edge cases may require verification. <strong>As part of our roadmap, we plan to add a confidence score to show how certain Judgy is about its answers</strong>. Always consult a certified judge for tournament play.
        </>
      )
    },
    {
      question: "Can I use Judgy at tournaments?",
      answer: "No. Judgy is designed for casual play, practice, and learning—not as an official ruling source at sanctioned events. Always call a judge at tournaments for official rulings."
    },
    {
      question: "What does Premium membership include?",
      answer: (
        <>
          Premium ($5/month) includes: conversation history (never lose a ruling), faster response times, search and favorites, export to PDF, and priority support. Plus, you help support Judgy's development! <strong>Note: Some premium features are still in development and will be available soon.</strong>
        </>
      )
    },
    {
      question: "How do I link to cards?",
      answer: "Use double square brackets around card names, like [[Lightning Bolt]]. Judgy will automatically fetch card information from Scryfall and display it with your question."
    },
    {
      question: "What if Judgy gives me a wrong answer?",
      answer: "Click the button below any response to flag it. We review all flagged answers to improve Judgy's accuracy. You can also email support@askjudgy.com with specific concerns."
    },
    {
      question: "Does Judgy know about new cards?",
      answer: "Judgy is updated regularly with new card releases and rules changes. However, it won't know about unreleased or spoiled cards until they're officially available."
    },
    {
      question: "Can I use Judgy in other languages?",
      answer: (
        <>
          Currently, Judgy works best in English. <strong>Multi-language support is on our roadmap—stay tuned!</strong>
        </>
      )
    },
    {
      question: "Is my data private?",
      answer: (
        <>
          Yes. We take privacy seriously. Your questions and conversations are stored securely and never shared. Premium users' conversation history is encrypted. See our <a href="/privacy" style={{ color: theme.brand.primary, textDecoration: 'none', fontWeight: typography.fontWeight.medium }}>Privacy Policy</a> for full details.
        </>
      )
    },
    {
      question: "How can I support Judgy?",
      answer: "Spread the word! The more people use Judgy, the more accurate its answers become. You can also support us by upgrading to Premium."
    },
    {
      question: "Can I integrate Judgy with Discord or other platforms?",
      answer: (
        <>
          <strong>Not yet, but it's coming!</strong> Discord bot integration is on our roadmap to provide better support for playgroups in the future.
        </>
      )
    },
    {
      question: "Who made Judgy?",
      answer: "Judgy is a fan-made project developed as a hobby by an MTG enthusiast who wants to help others navigate the game's complexities and provide a better way to settle rules disputes than endlessly searching through forums."
    },
    {
      question: "What are your Terms of Service?",
      answer: (
        <>
          By using Judgy, you agree to our <a href="/terms" style={{ color: theme.brand.primary, textDecoration: 'none', fontWeight: typography.fontWeight.medium }}>Terms of Service</a>. We keep them simple and fair. No surprises!
        </>
      )
    },
  ];

  return (
    <>
      <SimpleHeader />
      <div style={{
        backgroundColor: theme.bg.primary,
        position: 'fixed',
        top: '72px',
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        padding: spacing['3xl'],
        ...(isMobile() && {
          top: '70px',
          padding: spacing.lg,
        })
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          backgroundColor: 'white',
          padding: spacing['3xl'],
          borderRadius: '12px',
          boxShadow: theme.shadow.medium,
          marginBottom: spacing['3xl'],
          ...(isMobile() && {
            padding: spacing.lg,
            marginBottom: spacing.lg,
          })
        }}>
          {/* Back link */}
          <div style={{ marginBottom: spacing.lg }}>
            <a
              href="/"
              style={{
                color: theme.brand.primary,
                textDecoration: 'none',
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacing.sm,
              }}
            >
              ← Back to Judgy
            </a>
          </div>

          {/* Header */}
          <h1 style={{
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            marginBottom: spacing.md,
            color: theme.text.primary,
            fontFamily: typography.fontFamily.primary,
          }}>
            Frequently Asked Questions
          </h1>

          <p style={{
            fontSize: typography.fontSize.base,
            color: theme.text.secondary,
            marginBottom: spacing['2xl'],
            lineHeight: typography.lineHeight.relaxed,
          }}>
            Got questions about Judgy? We've got answers.
          </p>

          {/* FAQ Items */}
          {faqs.map((faq, index) => (
            <div
              key={index}
              style={{
                marginBottom: spacing.xl,
                paddingBottom: spacing.xl,
                borderBottom: index < faqs.length - 1 ? `1px solid ${theme.border.light}` : 'none',
              }}
            >
              <h2 style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.semibold,
                color: theme.text.primary,
                marginBottom: spacing.md,
                ...(isMobile() && {
                  fontSize: typography.fontSize.base,
                })
              }}>
                {faq.question}
              </h2>
              <div style={{
                fontSize: typography.fontSize.base,
                color: theme.text.secondary,
                lineHeight: typography.lineHeight.relaxed,
                margin: 0,
                ...(isMobile() && {
                  fontSize: typography.fontSize.sm,
                })
              }}>
                {typeof faq.answer === 'string' ? faq.answer : faq.answer}
              </div>
            </div>
          ))}

          {/* Contact CTA */}
          <div style={{
            backgroundColor: theme.bg.secondary,
            padding: spacing.xl,
            borderRadius: '8px',
            textAlign: 'center',
            marginTop: spacing['2xl'],
            ...(isMobile() && {
              padding: spacing.lg,
            })
          }}>
            <h3 style={{
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.semibold,
              marginBottom: spacing.md,
              color: theme.text.primary,
              ...(isMobile() && {
                fontSize: typography.fontSize.lg,
              })
            }}>
              Still have questions?
            </h3>
            <p style={{
              fontSize: typography.fontSize.base,
              color: theme.text.secondary,
              marginBottom: spacing.lg,
              ...(isMobile() && {
                fontSize: typography.fontSize.sm,
              })
            }}>
              We're here to help! Reach out anytime.
            </p>
            <a
              href="mailto:support@askjudgy.com"
              style={{
                color: theme.brand.primary,
                textDecoration: 'none',
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
              }}
            >
              support@askjudgy.com
            </a>
          </div>
        </div>
      </div>
    </>
  );
}